import { useCallback, useEffect, useReducer, useRef } from 'react';

import { FilePreviewProps } from '../filePreview/FilePreview';

export type ClosedFilePreviewState = {
  isOpen: false;
};
type AbstractOpenFilePreviewState = {
  isOpen: true;
  file: File;
  fileId: string;
  previewUrl: unknown;
};

export type OpenFilePreviewSuccessState = AbstractOpenFilePreviewState &
  Pick<FilePreviewProps, 'previewUrl'>;

export type OpenFilePreviewErrorState = {
  previewUrl: null;
} & AbstractOpenFilePreviewState;

export type FilePreviewState =
  | OpenFilePreviewSuccessState
  | OpenFilePreviewErrorState
  | ClosedFilePreviewState;

type FileSelectedAction = {
  type: 'file_selected';
  payload: Pick<OpenFilePreviewSuccessState, 'file' | 'previewUrl' | 'fileId'>;
};

type ClosedAction = {
  type: 'closed';
};

type ErrorAction = {
  type: 'error';
  payload: Pick<OpenFilePreviewErrorState, 'file' | 'fileId'>;
};

type Actions = FileSelectedAction | ClosedAction | ErrorAction;

function reducer(state: FilePreviewState, action: Actions): FilePreviewState {
  switch (action.type) {
    case 'file_selected': {
      const { fileId, file, previewUrl } = action.payload;
      return {
        isOpen: true,
        fileId,
        file,
        previewUrl,
      };
    }
    case 'error': {
      return {
        isOpen: true,
        file: action.payload.file,
        fileId: action.payload.fileId,
        previewUrl: null,
      };
    }
    case 'closed': {
      return {
        isOpen: false,
      };
    }
  }
}

export type FilePreviewStateResult = {
  setSelectedFile: (fileId: string, file: File) => void;
  close: () => void;
} & FilePreviewState;

export const useFilePreviewState: () => FilePreviewStateResult = () => {
  const [state, dispatch] = useReducer(reducer, {
    isOpen: false,
  });

  // Keep track of the current blob URL to revoke it when it changes or on unmount
  const currentBlobUrlRef = useRef<string | null>(null);

  const revokeBlobUrl = useCallback(() => {
    if (currentBlobUrlRef.current) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
  }, []);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      revokeBlobUrl();
    };
  }, [revokeBlobUrl]);

  const setSelectedFile = useCallback(
    (fileId: string, file: File) => {
      if (state.isOpen && state.file === file) {
        revokeBlobUrl();
        dispatch({ type: 'closed' });
        return;
      }

      // Revoke any existing blob URL before creating a new one
      revokeBlobUrl();

      // Create a blob URL - no size limit unlike data URLs
      const blobUrl = URL.createObjectURL(file);
      currentBlobUrlRef.current = blobUrl;

      dispatch({
        type: 'file_selected',
        payload: {
          fileId,
          file,
          previewUrl: blobUrl,
        },
      });
    },
    [state.isOpen, state.isOpen ? state.file : null, revokeBlobUrl]
  );

  const close = useCallback(() => {
    revokeBlobUrl();
    dispatch({ type: 'closed' });
  }, [revokeBlobUrl]);

  return {
    ...state,
    close,
    setSelectedFile,
  };
};
